import { CloseOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
    Button,
    Col,
    Drawer,
    Form,
    type FormInstance,
    Input,
    Row,
    Tooltip,
    Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type {
    ProdutoBody,
    ProdutoBodyTabelaComissaoExcecao,
} from '@/features/produto/types/ProdutoBody';
import type { Dialog } from '@/std/dialog/types';
import { Container } from '@/components/Container';
import { InputNumber } from '@/components/input/InputNumber';
import type { RowComissaoProduto } from './produtoTabComissaoData';

export function ProdutoTabComissaoDrawer({
    form,
    dialog,
    handleChangeExcecao,
    handleCloseEdit,
}: {
    form: FormInstance<FormProdutoValues>;
    dialog: Dialog<RowComissaoProduto>;
    handleChangeExcecao: (value: boolean, values?: ProdutoBodyTabelaComissaoExcecao) => void;
    handleCloseEdit: () => void;
}) {
    const [formComissaoProduto] = Form.useForm<ProdutoBodyTabelaComissaoExcecao>();

    const { open, data } = dialog.state;

    const [showComissao, setShowComissao] = useState(false);
    const [hadChanged, setHadChanged] = useState(false);

    const handleInfoComissao = (): void => {
        setShowComissao(true);
        setHadChanged(true);
    };

    const excluir = (): void => {
        formComissaoProduto.resetFields();
        setShowComissao(false);
        setHadChanged(true);

        const tce_idpk: number | undefined = data?.tabela_comissao_excecao
            ? data?.tabela_comissao_excecao[0]?.tce_idpk
            : undefined;

        const produtosFormValues = form.getFieldsValue(true) as ProdutoBody;
        const tabelaComissao = produtosFormValues.tabela_comissao;
        const findIndex = (tabelaComissao || []).findIndex((tp) => tp.tac_idpk === data?.tac_idpk);

        if (tce_idpk) {
            if (findIndex >= 0 && tabelaComissao) {
                form.setFields([
                    {
                        name: ['tabela_comissao', findIndex],
                        value: {
                            ...tabelaComissao[findIndex],
                            tabela_comissao_excecao: [
                                {
                                    ...tabelaComissao[findIndex]?.tabela_comissao_excecao[0],
                                    excluir: 'S',
                                },
                            ],
                        },
                    },
                ]);

                handleChangeExcecao(false, undefined);

                return;
            }

            const newtabelaComissao = tabelaComissao || [];

            newtabelaComissao.push({
                tabela_comissao_excecao: [
                    {
                        excluir: 'S',
                        tce_idpk,
                    },
                ],
                tac_idpk: data?.tac_idpk || 0,
            });

            form.setFields([
                {
                    name: 'tabela_comissao',
                    value: newtabelaComissao,
                },
            ]);

            handleChangeExcecao(false, undefined);
        } else {
            if (!tabelaComissao) {
                return;
            }

            if (findIndex >= 0) {
                tabelaComissao.splice(findIndex, 1);

                form.setFields([
                    {
                        name: 'tabela_comissao',
                        value: tabelaComissao,
                    },
                ]);

                handleChangeExcecao(false, undefined);
            }
        }
    };

    const onFinish = (): void => {
        const formValues = formComissaoProduto.getFieldsValue(
            true,
        ) as ProdutoBodyTabelaComissaoExcecao;

        const produtosFormValues = form.getFieldsValue(true) as ProdutoBody;
        const tabelaComissao = produtosFormValues.tabela_comissao;

        const findIndex = (tabelaComissao || []).findIndex((tp) => tp.tac_idpk === data?.tac_idpk);

        if (findIndex >= 0) {
            form.setFields([
                {
                    name: ['tabela_comissao', findIndex],
                    value: {
                        tabela_comissao_excecao: [formValues],
                        tac_idpk: data?.tac_idpk || 0,
                    },
                },
            ]);

            handleChangeExcecao(true, formValues);

            return;
        }

        const newtabelaComissao = tabelaComissao || [];

        newtabelaComissao.push({
            tabela_comissao_excecao: [formValues],
            tac_idpk: data?.tac_idpk || 0,
        });

        form.setFields([
            {
                name: 'tabela_comissao',
                value: newtabelaComissao,
            },
        ]);

        handleChangeExcecao(true, formValues);
    };

    const closeDrawer = (): void => {
        if (hadChanged && showComissao) {
            onFinish();
        }

        dialog.actions.close();
        setShowComissao(false);
        setHadChanged(false);
        handleCloseEdit();
        formComissaoProduto.resetFields();
    };

    useEffect(() => {
        if (data?.tabela_comissao_excecao?.[0]) {
            if (!data?.tabela_comissao_excecao[0]?.tce_percentual) {
                return;
            }

            setShowComissao(true);

            formComissaoProduto.setFields([
                {
                    name: 'tce_idpk',
                    value: data?.tabela_comissao_excecao[0]?.tce_idpk,
                },
                {
                    name: 'tce_percentual',
                    value: data?.tabela_comissao_excecao[0]?.tce_percentual,
                },
            ]);
        }
    }, [data, formComissaoProduto]);

    return (
        <Drawer
            destroyOnHidden={true}
            onClose={closeDrawer}
            open={open}
            placement='right'
            title='Regra de Comissão'
            zIndex={9999}
        >
            <Container>
                <div className='flex'>
                    <div className='pb-1 pl-0 pr-2 pt-1'>
                        <div>Produto:</div>
                        <div>Tabela de Comissão:</div>
                    </div>

                    <div className='pb-1 pl-2 pr-0 pt-1'>
                        <div>
                            <strong>{data?.produto?.pro_descricao || ''}</strong>
                        </div>
                        <div>
                            <strong>{data?.tac_descricao || ''}</strong>
                        </div>
                    </div>
                </div>
            </Container>

            <Row justify='center'>
                <Col span={24}>
                    <Typography.Title level={4} style={{ marginTop: '10px', width: '100%' }}>
                        <span style={{ marginRight: '8px' }}>Comissão Diferenciada</span>

                        <Tooltip
                            title='É possível usar um percentual diferente especificamente para este produto dentro desta comissão.'
                            zIndex={99999}
                        >
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </Typography.Title>
                </Col>

                {!showComissao && (
                    <Col span={24}>
                        <Container>
                            <Button
                                block={true}
                                htmlType='button'
                                onClick={handleInfoComissao}
                                type='primary'
                            >
                                Informar comissão diferenciada
                            </Button>
                        </Container>
                    </Col>
                )}

                {showComissao && (
                    <Col span={24}>
                        <Container>
                            <Form
                                form={formComissaoProduto}
                                id='formComissaoProduto'
                                initialValues={{
                                    tce_percentual: 0,
                                }}
                                labelAlign='left'
                                layout='vertical'
                                name='formComissaoProduto'
                                onFinish={onFinish}
                                onValuesChange={() => setHadChanged(true)}
                            >
                                <Form.Item hidden={true} name='tce_idpk' noStyle={true}>
                                    <Input hidden={true} />
                                </Form.Item>

                                <Row gutter={[10, 24]} style={{ position: 'relative' }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: '10px',
                                        }}
                                    >
                                        <Tooltip title='Não informar o percentual.'>
                                            <Button
                                                icon={<DeleteOutlined />}
                                                onClick={excluir}
                                                shape='circle'
                                                type='default'
                                            />
                                        </Tooltip>
                                    </div>

                                    <Col span={24}>
                                        <Form.Item
                                            label='Usar esta comissão para esta regra'
                                            name='tce_percentual'
                                            rules={[
                                                { message: 'Informe um valor!', required: true },
                                            ]}
                                            style={{ margin: 0, marginTop: '20px' }}
                                        >
                                            <InputNumber decimalScale={2} type='%' />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form>
                        </Container>
                    </Col>
                )}

                <Col span={24} style={{ textAlign: 'center' }}>
                    <Button
                        icon={<CloseOutlined />}
                        onClick={() => closeDrawer()}
                        style={{ marginTop: 20 }}
                        type='default'
                    >
                        Voltar
                    </Button>
                </Col>
            </Row>
        </Drawer>
    );
}
